// src/admin/components/shared/UnifiedReceipt.jsx
import React, { useRef, useState, useEffect } from 'react';
import {
    RiPrinterLine,
    RiCloseLine,
    RiTranslate2,
    RiDownloadLine
} from 'react-icons/ri';
import { formatCurrency } from '../../../models/paymentModel';
import { getUserProfile } from '../../../models/userModel';
const defaultLogo = '/assets/IMG_2648.PNG';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const UnifiedReceipt = ({
    data,
    type = 'rent',
    branding = {},
    onClose,
    autoPrint = false
}) => {
    const [language, setLanguage] = useState(data.preferredLanguage || 'en');
    const [isPrinting, setIsPrinting] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [dateMode, setDateMode] = useState('current');
    const [paperSize, setPaperSize] = useState('a5');
    const printContentRef = useRef(null);

    const isRTL = language === 'ar' || language === 'ku';
    const isKu = language === 'ku';
    const isA5 = paperSize === 'a5';

    const getReceiptStyles = (isRTL, language, paperSize) => `
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800&family=Inter:wght@300;400;500;600;700;800&display=swap');

        :root {
          --primary-color: #0d9488;
          --text-main: #111827;
          --text-light: #6b7280;
          --border-color: #e5e7eb;
          --bg-light: #f9fafb;
        }

        * {
          box-sizing: border-box;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body {
          font-family: ${language === 'ar' ? "'Cairo', 'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif" : (language === 'ku' ? "'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif" : "'Inter', sans-serif")};
          margin: 0;
          padding: 0;
          color: var(--text-main);
          background-color: white;
          line-height: 1.5;
        }

        @page {
          size: ${paperSize === 'a5' ? 'A5 landscape' : 'A4 portrait'};
          margin: 0;
        }

        .print-container {
          width: 210mm;
          min-height: ${paperSize === 'a5' ? '148mm' : '297mm'};
          padding: ${paperSize === 'a5' ? '8mm 14mm' : '20mm 20mm'};
          margin: 0 auto;
          position: relative;
          background: white;
          overflow: hidden;
        }

        .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: ${paperSize === 'a5' ? '60px' : '100px'};
            color: rgba(13, 148, 136, 0.05);
            font-weight: 800;
            white-space: nowrap;
            pointer-events: none;
            z-index: 0;
            text-transform: uppercase;
        }

        /* ── Header ── */
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          direction: ltr;
          padding-bottom: ${paperSize === 'a5' ? '8px' : '14px'};
          margin-bottom: 0;
          position: relative;
          z-index: 1;
        }

        .logo-img {
          max-height: ${paperSize === 'a5' ? '56px' : '80px'};
          max-width: ${paperSize === 'a5' ? '140px' : '200px'};
          object-fit: contain;
        }

        /* ── Accent line ── */
        .accent-line {
          height: 3px;
          background: var(--primary-color);
          margin-bottom: ${paperSize === 'a5' ? '10px' : '16px'};
          position: relative;
          z-index: 1;
        }

        /* ── Title ── */
        .receipt-title {
          text-align: center;
          font-size: ${paperSize === 'a5' ? '20px' : '26px'};
          font-weight: 800;
          color: var(--text-main);
          margin-bottom: ${paperSize === 'a5' ? '10px' : '16px'};
          font-family: ${language === 'ar' ? "'Cairo', sans-serif" : (language === 'ku' ? "'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif" : "'Inter', sans-serif")};
          position: relative;
          z-index: 1;
        }

        /* ── Info row (fill-lines + boxed fields) ── */
        .info-row {
          display: flex;
          gap: ${paperSize === 'a5' ? '16px' : '24px'};
          margin-bottom: ${paperSize === 'a5' ? '12px' : '20px'};
          align-items: flex-start;
          flex-direction: ${isRTL ? 'row' : 'row-reverse'};
          position: relative;
          z-index: 1;
        }

        .fill-lines {
          flex: 1;
        }

        .fill-row {
          display: flex;
          align-items: baseline;
          gap: 6px;
          margin-bottom: ${paperSize === 'a5' ? '8px' : '12px'};
        }

        .fill-label {
          font-size: ${paperSize === 'a5' ? '11px' : '13px'};
          font-weight: 600;
          white-space: nowrap;
          color: var(--text-main);
        }

        .fill-dashes {
          flex: 1;
          border-bottom: 1px dashed #bbb;
          min-width: 30px;
          margin-bottom: 2px;
        }

        .fill-value {
          font-size: ${paperSize === 'a5' ? '11px' : '13px'};
          font-weight: 700;
          color: var(--text-main);
          word-break: break-word;
          text-align: ${isRTL ? 'right' : 'left'};
        }

        .info-boxes {
          min-width: ${paperSize === 'a5' ? '150px' : '180px'};
          flex-shrink: 0;
        }

        .info-box-item {
          border: 1px solid var(--border-color);
          padding: ${paperSize === 'a5' ? '4px 8px' : '6px 10px'};
          margin-bottom: 5px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 2px;
          border-radius: 4px;
          background: var(--bg-light);
        }

        .info-box-label {
          font-size: ${paperSize === 'a5' ? '10px' : '12px'};
          color: var(--text-light);
          white-space: nowrap;
        }

        .info-box-value {
          font-size: ${paperSize === 'a5' ? '10px' : '12px'};
          font-weight: 700;
          color: var(--text-main);
        }

        /* ── Table ── */
        table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            margin-bottom: ${paperSize === 'a5' ? '12px' : '24px'};
            border: 1px solid var(--border-color);
            border-radius: 6px;
            overflow: hidden;
            position: relative;
            z-index: 1;
        }

        th {
            background-color: var(--bg-light);
            color: var(--text-main);
            text-align: ${isRTL ? 'right' : 'left'};
            padding: ${paperSize === 'a5' ? '6px 10px' : '10px 14px'};
            font-size: ${paperSize === 'a5' ? '9px' : '10px'};
            font-weight: 700;
            text-transform: ${isRTL ? 'none' : 'uppercase'};
            letter-spacing: ${isRTL ? '0' : '0.5px'};
            border-bottom: 2px solid var(--primary-color);
        }

        td {
            padding: ${paperSize === 'a5' ? '6px 10px' : '10px 14px'};
            border-bottom: 1px solid var(--border-color);
            font-size: ${paperSize === 'a5' ? '11px' : '13px'};
            text-align: ${isRTL ? 'right' : 'left'};
        }

        tbody tr:last-child td {
            border-bottom: none;
        }

        /* ── Installments ── */
        .installments-section {
          margin-bottom: ${paperSize === 'a5' ? '10px' : '18px'};
          position: relative;
          z-index: 1;
        }

        .installments-title {
          font-size: ${paperSize === 'a5' ? '9px' : '11px'};
          font-weight: 700;
          color: var(--text-light);
          text-transform: ${isRTL ? 'none' : 'uppercase'};
          letter-spacing: ${isRTL ? '0' : '0.5px'};
          margin-bottom: 5px;
        }

        .inst-paid td {
          background-color: rgba(16, 185, 129, 0.04);
        }

        .badge-paid {
          display: inline-block;
          padding: 1px 6px;
          background: rgba(16, 185, 129, 0.15);
          color: #059669;
          border-radius: 4px;
          font-size: ${paperSize === 'a5' ? '8px' : '10px'};
          font-weight: 700;
        }

        .badge-pending {
          display: inline-block;
          padding: 1px 6px;
          background: rgba(245, 158, 11, 0.12);
          color: #d97706;
          border-radius: 4px;
          font-size: ${paperSize === 'a5' ? '8px' : '10px'};
          font-weight: 700;
        }

        /* ── Signatures ── */
        .sig-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${paperSize === 'a5' ? '24px' : '60px'};
          margin-top: ${paperSize === 'a5' ? '14px' : '36px'};
          padding: 0 10px;
          position: relative;
          z-index: 1;
        }

        .signature-box {
            text-align: center;
        }

        .signature-line {
            border-top: 1px solid var(--border-color);
            margin-top: ${paperSize === 'a5' ? '28px' : '44px'};
            padding-top: 6px;
            font-size: ${paperSize === 'a5' ? '10px' : '11px'};
            font-weight: 600;
            color: var(--text-light);
            text-transform: ${isRTL ? 'none' : 'uppercase'};
            letter-spacing: ${isRTL ? '0' : '0.5px'};
        }

        /* ── Print footer (for print only) ── */
        .print-footer {
            display: none;
        }

        @media print {
            body { background: none; }
            .print-container { margin: 0; border: none; box-shadow: none; width: 100%; }
        }
    `;

    function handlePrint() {
        setIsPrinting(true);
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'absolute';
        printFrame.style.top = '-9999px';
        printFrame.style.left = '-9999px';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        document.body.appendChild(printFrame);

        const contentElement = printContentRef.current;
        const contentHtml = contentElement.innerHTML.replace(/<style[\s\S]*?<\/style>/gi, '');

        const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
        frameDoc.open();
        frameDoc.write(`
      <!DOCTYPE html>
      <html dir="${isRTL ? 'rtl' : 'ltr'}" lang="${language}">
        <head>
          <title>${isRTL ? (isKu ? 'پسوولە' : 'إيصال') : 'Receipt'}</title>
          <meta charset="utf-8">
          <style>
            ${getReceiptStyles(isRTL, language, paperSize)}
          </style>
        </head>
        <body>
          <div class="print-container" dir="${isRTL ? 'rtl' : 'ltr'}">
            ${contentHtml}
          </div>
        </body>
      </html>
    `);
        frameDoc.close();

        printFrame.onload = () => {
            try {
                setTimeout(() => {
                    printFrame.contentWindow.focus();
                    printFrame.contentWindow.print();
                    setTimeout(() => {
                        document.body.removeChild(printFrame);
                        setIsPrinting(false);
                    }, 500);
                }, 500);
            } catch (err) {
                console.error('Print failed:', err);
                document.body.removeChild(printFrame);
                setIsPrinting(false);
                alert('Printing failed. Please try again.');
            }
        };
    }

    useEffect(() => {
        if (type === 'sale' && data.installments && data.installments.length >= 3) {
            setPaperSize('a4');
        }
    }, [type, data.installments]);

    useEffect(() => {
        let isMounted = true;
        async function fetchUserProfile() {
            try {
                const response = await getUserProfile();
                if (isMounted && response && response.user) {
                    setCurrentUser(response.user);
                }
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
            }
        }
        fetchUserProfile();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (autoPrint && data) {
            const timer = setTimeout(() => {
                if (typeof handlePrint === 'function') {
                    handlePrint();
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [autoPrint, data]);

    async function handleExportPDF() {
        setIsPrinting(true);
        try {
            const element = printContentRef.current;
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                allowTaint: true
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: isA5 ? 'landscape' : 'portrait',
                unit: 'mm',
                format: isA5 ? [210, 148] : 'a4',
                compress: true
            });
            const pageWidth = 210;
            const pageHeight = isA5 ? 148 : 297;
            const imgWidth = pageWidth;
            const imgHeight = canvas.height * imgWidth / canvas.width;
            const finalHeight = Math.min(imgHeight, pageHeight);
            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, finalHeight);

            const tenantName = data.buyerName || data.tenantName || data.ownerName || 'receipt';
            const prefix = type === 'rent' ? 'Invoice' : (type === 'sale' ? 'SaleReceipt' : 'ServiceReceipt');
            pdf.save(`${prefix}-${tenantName}.pdf`);
        } catch (err) {
            console.error('PDF export failed:', err);
            alert('Failed to export PDF. Please try again.');
        }
        setIsPrinting(false);
    }

    function toggleLanguage() {
        setLanguage(prev => {
            if (prev === 'en') return 'ar';
            if (prev === 'ar') return 'ku';
            return 'en';
        });
    }

    function t(key) {
        const translations = {
            invoiceTitle: { en: 'Tenant Invoice', ar: 'وصل قبض', ku: 'پسوولەی کرێ' },
            serviceTitle: { en: 'Service Receipt', ar: 'وصل خدمات', ku: 'پسوولەی خزمەتگوزاری' },
            saleTitle: { en: 'Sale Receipt', ar: 'وصل قبض', ku: 'پسوولەی فرۆشتن' },
            receiptNo: { en: 'Receipt No', ar: 'رقم الوصل', ku: 'ژمارەی پسوولە' },
            invoiceNo: { en: 'Invoice No', ar: 'رقم الفاتورة', ku: 'ژمارەی فاکتۆر' },
            date: { en: 'Date', ar: 'التاريخ', ku: 'ڕێکەوت' },
            description: { en: 'Description', ar: 'الوصف', ku: 'وەسف' },
            details: { en: 'Details', ar: 'التفاصيل', ku: 'وردەکاری' },
            nameLabel: {
                en: type === 'rent' ? 'Tenant Name' : (type === 'sale' ? 'Buyer Name' : 'Resident Name'),
                ar: type === 'rent' ? 'اسم المستأجر' : (type === 'sale' ? 'اسم المشتري' : 'اسم الساكن'),
                ku: type === 'rent' ? 'ناوی کرێچی' : (type === 'sale' ? 'ناوی کڕیار' : 'ناوی نیشتەجێبوون')
            },
            building: { en: 'Building', ar: 'البناية', ku: 'بینا' },
            apartment: { en: 'Showroom', ar: 'المعرض', ku: 'پێشانگا' },
            periodLabel: {
                en: type === 'rent' ? 'Contract Date' : (type === 'sale' ? 'Payment Plan' : 'Service Period'),
                ar: type === 'rent' ? 'تاريخ العقد' : (type === 'sale' ? 'طریقه الدفع' : 'فترة الخدمة'),
                ku: type === 'rent' ? 'بەرواری گرێبەست' : (type === 'sale' ? 'پلانی پارەدان' : 'ماوەی خزمەتگوزاری')
            },
            paymentDate: { en: 'Payment Date', ar: 'تاريخ الدفع', ku: 'ڕێکەوتی پارەدان' },
            amount: { en: 'Total Amount', ar: 'المبلغ الإجمالي', ku: 'کۆی گشتی پارە' },
            signature: { en: type === 'rent' ? 'Tenant Signature' : 'Customer Signature', ar: type === 'rent' ? 'توقيع المستأجر' : 'المستلم', ku: type === 'rent' ? 'واژۆی کرێچی' : 'واژۆی کڕیار' },
            responsible: { en: 'Authorized Signature', ar: 'المسلّم', ku: 'واژۆی بەشی ژمێریاری' },
            print: { en: 'Print', ar: 'طباعة', ku: 'چاپکردن' },
            exportPDF: { en: 'Export PDF', ar: 'تصدير PDF', ku: 'هەناردەکردنی PDF' },
            toggleLanguage: { en: 'العربية', ar: 'کوردی', ku: 'English' },
            dateCurrent: { en: 'Today\'s Date', ar: 'التاريخ الحالي', ku: 'ئەمرۆی ڕێکەوت' },
            dateInstallment: { en: '1st Installment Date', ar: 'تاريخ أول قسط', ku: 'ڕێکەوتی یەکەم قیست' },
        };
        return translations[key]?.[language] || key;
    }

    function getResponsibleName() {
        if (currentUser) {
            return `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
        }
        return '';
    }

    function getCustomerName() {
        if (type === 'rent') return data.tenantName || '';
        return data.ownerName || data.buyerName || '';
    }

    function formatDisplayDate(dateString = new Date()) {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isRTL) {
            return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
        } else {
            return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
        }
    }

    function formatPaymentPeriod() {
        if (type === 'sale') {
            const numInst = data.numInstallments || data.installments?.length || 0;
            if (numInst > 1) {
                return isRTL
                    ? (language === 'ku' ? `${numInst} قیست` : `${numInst} أقساط`)
                    : `${numInst} Installments`;
            }
            return isRTL ? (language === 'ku' ? 'پارەی تەواو' : 'دفع كامل') : 'Full Payment';
        }

        if (type === 'service' && data.month !== undefined && data.year !== undefined) {
            const arMonths = ['كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران', 'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'];
            const kuMonths = ['کانونی دووەم', 'شوبات', 'ئازار', 'نیسان', 'ئایار', 'حوزەیران', 'تەممووز', 'ئاب', 'ئەیلوول', 'تشرینی یەکەم', 'تشرینی دووەم', 'کانونی یەکەم'];
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const monthIndex = parseInt(data.month) - 1;
            const monthName = language === 'ku' ? kuMonths[monthIndex] : (language === 'ar' ? arMonths[monthIndex] : months[monthIndex]);
            return `${monthName} ${data.year}`;
        }

        if (!data.months) return '';
        try {
            const monthsList = Array.isArray(data.months) ? data.months : JSON.parse(data.months);
            if (monthsList.length === 0) return '';

            const formatMonth = (m) => {
                if (typeof m === 'number' || (typeof m === 'string' && !isNaN(m) && !m.includes('-'))) {
                    const monthIndex = parseInt(m) - 1;
                    const date = new Date();
                    if (data.paymentDate) {
                        const pDate = new Date(data.paymentDate);
                        date.setFullYear(pDate.getFullYear());
                    }
                    date.setMonth(monthIndex);
                    return new Intl.DateTimeFormat(language === 'ku' ? 'ku-IQ' : (language === 'ar' ? 'ar-EG' : 'en-US'), {
                        month: 'long',
                        year: 'numeric'
                    }).format(date);
                }
                return formatDisplayDate(m);
            };

            if (monthsList.length >= 2) {
                return `${formatMonth(monthsList[0])} - ${formatMonth(monthsList[monthsList.length - 1])}`;
            }
            return formatMonth(monthsList[0]);
        } catch (err) {
            return '';
        }
    }

    function getMetaDate() {
        if (dateMode === 'installment') {
            if (type === 'sale' && data.installments && data.installments.length > 0) {
                return formatDisplayDate(data.installments[0].dueDate);
            }
            if (data.paymentDate) return formatDisplayDate(data.paymentDate);
            try {
                const months = Array.isArray(data.months) ? data.months : JSON.parse(data.months || '[]');
                if (months.length > 0) return formatDisplayDate(months[0]);
            } catch (e) {}
        }
        return formatDisplayDate();
    }

    const logo = branding.logoUrl || defaultLogo;
    const titleKey = type === 'rent' ? 'invoiceTitle' : (type === 'sale' ? 'saleTitle' : 'serviceTitle');
    const noKey = type === 'rent' ? 'invoiceNo' : 'receiptNo';

    const previewStyle = {
        fontFamily: language === 'ar' ? "'Cairo', 'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif" : (language === 'ku' ? "'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif" : "'Inter', sans-serif"),
        direction: isRTL ? 'rtl' : 'ltr',
        color: '#111827',
        padding: isA5 ? '8mm 14mm' : '20mm 20mm',
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className={`bg-zinc-100 rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden flex flex-col max-h-[95vh] ${isRTL ? 'rtl' : 'ltr'}`}
                style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Tools Header */}
                <div className="p-4 border-b border-zinc-200 flex justify-between items-center bg-white">
                    <h2 className="text-xl font-extrabold text-zinc-900 flex items-center gap-2">
                        <div className="w-1.5 h-6 bg-teal-500 rounded-full"></div>
                        {t(titleKey)}
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPaperSize(s => s === 'a4' ? 'a5' : 'a4')}
                            className="px-3 py-1.5 rounded-lg bg-zinc-100 text-zinc-700 hover:bg-zinc-200 text-xs font-bold transition-all"
                            disabled={isPrinting}
                            title="Toggle paper size"
                        >
                            {isA5 ? 'A5' : 'A4'}
                        </button>
                        <button
                            onClick={() => setDateMode(d => d === 'current' ? 'installment' : 'current')}
                            className="px-3 py-1.5 rounded-lg bg-zinc-100 text-zinc-700 hover:bg-zinc-200 flex items-center text-xs font-bold transition-all"
                            disabled={isPrinting}
                            title={dateMode === 'current' ? t('dateInstallment') : t('dateCurrent')}
                        >
                            {dateMode === 'current' ? t('dateInstallment') : t('dateCurrent')}
                        </button>
                        <button
                            onClick={toggleLanguage}
                            className="px-3 py-1.5 rounded-lg bg-zinc-100 text-zinc-700 hover:bg-zinc-200 flex items-center text-xs font-bold transition-all"
                            disabled={isPrinting}
                        >
                            <RiTranslate2 className={isRTL ? "ml-1.5" : "mr-1.5"} size={16} />
                            {t('toggleLanguage')}
                        </button>
                        <button
                            onClick={handleExportPDF}
                            className="px-3 py-1.5 rounded-lg bg-zinc-800 text-white hover:bg-zinc-900 flex items-center text-xs font-bold transition-all shadow-sm"
                            disabled={isPrinting}
                        >
                            <RiDownloadLine className={isRTL ? "ml-1.5" : "mr-1.5"} size={16} />
                            {t('exportPDF')}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="px-3 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 flex items-center text-xs font-bold transition-all shadow-sm"
                            disabled={isPrinting}
                        >
                            <RiPrinterLine className={isRTL ? "ml-1.5" : "mr-1.5"} size={16} />
                            {t('print')}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-lg bg-zinc-100 text-zinc-500 hover:bg-zinc-200 transition-all ml-2"
                            disabled={isPrinting}
                        >
                            <RiCloseLine size={20} />
                        </button>
                    </div>
                </div>

                {/* Preview Area */}
                <div className="overflow-y-auto bg-zinc-200 p-10 flex-1 flex flex-col items-center">
                    <div
                        className={`bg-white shadow-[0_0_50px_rgba(0,0,0,0.1)] w-[210mm] ${isA5 ? 'min-h-[148mm]' : 'min-h-[297mm]'} relative pointer-events-none print-container`}
                        ref={printContentRef}
                        style={previewStyle}
                    >
                        <style>{getReceiptStyles(isRTL, language, paperSize)}</style>

                        {/* Watermark */}
                        <div className="watermark">{isRTL ? (isKu ? 'کۆپیکراوە' : 'نسخة') : 'OFFICIAL'}</div>

                        {/* Header: logo left, optional logo right */}
                        <div className="header">
                            <img src={logo} alt="Logo" className="logo-img" />
                            {branding.secondaryLogoUrl && (
                                <img src={branding.secondaryLogoUrl} alt="" className="logo-img" />
                            )}
                        </div>

                        {/* Accent line */}
                        <div className="accent-line" />

                        {/* Title */}
                        <div className="receipt-title">{t(titleKey)}</div>

                        {/* Info row */}
                        <div className="info-row">
                            {/* Fill-lines side (start side in RTL = right visually) */}
                            <div className="fill-lines">
                                <div className="fill-row">
                                    <span className="fill-label">{t('nameLabel')}:</span>
                                    <span className="fill-value" style={{ marginInlineStart: '6px' }}>{getCustomerName()}</span>
                                </div>
                                <div className="fill-row">
                                    <span className="fill-label">{t('amount')}:</span>
                                    <span className="fill-value" style={{ marginInlineStart: '6px' }}>{formatCurrency(data.amount || data.totalPrice)}</span>
                                </div>
                                <div className="fill-row">
                                    <span className="fill-label">{t('periodLabel')}:</span>
                                    <span className="fill-value" style={{ marginInlineStart: '6px' }}>{formatPaymentPeriod()}</span>
                                </div>
                            </div>

                            {/* Boxed fields side (end side in RTL = left visually) */}
                            <div className="info-boxes">
                                <div className="info-box-item">
                                    <span className="info-box-label">{t(noKey)}:</span>
                                    <span className="info-box-value">#{data.saleNumber ?? data.id ?? '-'}</span>
                                </div>
                                <div className="info-box-item">
                                    <span className="info-box-label">{t('date')}:</span>
                                    <span className="info-box-value">{getMetaDate()}</span>
                                </div>
                            </div>
                        </div>

                        {/* Property Table */}
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('building')}</th>
                                    <th>{t('apartment')}</th>
                                    <th>{t('description')}</th>
                                    <th>{t('amount')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{data.buildingName || '-'}</td>
                                    <td>{data.apartmentNumber || '-'}</td>
                                    <td>{formatPaymentPeriod()}</td>
                                    <td style={{ fontWeight: 700 }}>{formatCurrency(data.amount || data.totalPrice)}</td>
                                </tr>
                                {data.notes && (
                                    <tr>
                                        <td colSpan={4} style={{ color: '#6b7280', fontSize: '11px', fontStyle: 'italic' }}>{data.notes}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Installments — sale type with multiple installments */}
                        {type === 'sale' && data.installments && data.installments.length > 1 && (
                            <div className="installments-section">
                                <div className="installments-title">
                                    {language === 'ar' ? 'جدول الأقساط' : language === 'ku' ? 'خشتەی قیستەکان' : 'Installment Schedule'}
                                </div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '8%' }}>#</th>
                                            <th>{language === 'ar' ? 'تاريخ الاستحقاق' : language === 'ku' ? 'ڕێکەوتی مووچە' : 'Due Date'}</th>
                                            <th>{language === 'ar' ? 'المبلغ' : language === 'ku' ? 'بڕی پارە' : 'Amount'}</th>
                                            <th>{language === 'ar' ? 'الحالة' : language === 'ku' ? 'بارودۆخ' : 'Status'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.installments.map((inst, idx) => (
                                            <tr key={inst.id || idx} className={inst.status === 'paid' ? 'inst-paid' : ''}>
                                                <td>{idx + 1}</td>
                                                <td>{formatDisplayDate(inst.dueDate)}</td>
                                                <td>{formatCurrency(inst.amount)}</td>
                                                <td>
                                                    <span className={inst.status === 'paid' ? 'badge-paid' : 'badge-pending'}>
                                                        {inst.status === 'paid'
                                                            ? (language === 'ar' ? 'مدفوع' : language === 'ku' ? 'دراوە' : 'Paid')
                                                            : (language === 'ar' ? 'معلق' : language === 'ku' ? 'چاوەڕێ' : 'Pending')}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Signatures */}
                        <div className="sig-row">
                            <div className="signature-box">
                                <p style={{ margin: '0 0 0 0', fontWeight: 700, fontSize: isA5 ? '12px' : '14px' }}>{getCustomerName()}</p>
                                <div className="signature-line">{t('signature')}</div>
                            </div>
                            <div className="signature-box">
                                <p style={{ margin: '0 0 0 0', fontWeight: 700, fontSize: isA5 ? '12px' : '14px' }}>{getResponsibleName()}</p>
                                <div className="signature-line">{t('responsible')}</div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Progress Overlay */}
                {isPrinting && (
                    <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center z-[100] animate-in fade-in duration-300">
                        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <span className="text-lg font-black text-zinc-900 tracking-widest uppercase">
                            {language === 'ku' ? 'ئامادەکردن...' : (language === 'ar' ? 'جاري التحضير...' : 'Preparing...')}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};


export default UnifiedReceipt;
